import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { v4 as uuidv4 } from "uuid";

// Sandbox mode: when active, all reads/writes go to localStorage instead of
// Firestore. Used for trying things out without touching production data.
const STORAGE_MODE_KEY = "__alquileres_storage_mode__";
const SANDBOX_PREFIX = "__sandbox__";

export const isSandboxMode = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_MODE_KEY) === "sandbox";
};

export const setSandboxMode = (active) => {
  if (active) window.localStorage.setItem(STORAGE_MODE_KEY, "sandbox");
  else window.localStorage.removeItem(STORAGE_MODE_KEY);
};

// ---- Sandbox (localStorage) backend ----

const sandboxKey = (coll) => `${SANDBOX_PREFIX}${coll}`;
const sandboxRead = (coll) => {
  try {
    const raw = window.localStorage.getItem(sandboxKey(coll));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const sandboxWrite = (coll, items) =>
  window.localStorage.setItem(sandboxKey(coll), JSON.stringify(items));
const sandboxReadDoc = (coll, docId) => {
  const items = sandboxRead(coll);
  return items.find((i) => i.idFirebase === docId) || null;
};
const sandboxWriteDoc = (coll, docId, data) => {
  const items = sandboxRead(coll);
  const idx = items.findIndex((i) => i.idFirebase === docId);
  const next = { idFirebase: docId, ...data };
  if (idx >= 0) items[idx] = { ...items[idx], ...data, idFirebase: docId };
  else items.push(next);
  sandboxWrite(coll, items);
};

// Clear every sandbox collection. Used by "reset sandbox" UI.
export const clearSandbox = () => {
  for (let i = window.localStorage.length - 1; i >= 0; i--) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(SANDBOX_PREFIX)) window.localStorage.removeItem(k);
  }
};

// Snapshot a list of items (already fetched from Firestore) into the sandbox.
export const seedSandbox = (coll, items) => sandboxWrite(coll, items || []);
export const seedSandboxSingleton = (coll, docId, data) =>
  sandboxWrite(coll, [{ idFirebase: docId, ...data }]);

// Firestore rejects fields whose value is undefined. Strip them before write.
// Nested objects are recursed; null is preserved.
export const cleanData = (data) => {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) return data.map(cleanData);
  if (typeof data !== "object") return data;
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined) continue;
    out[k] = typeof v === "object" && v !== null ? cleanData(v) : v;
  }
  return out;
};

export const collRef = (name) => collection(db, name);
export const docRef = (collName, id) => doc(db, collName, id);

// ---- Public API: each one branches on isSandboxMode() ----

export const fetchAll = async (collName) => {
  if (isSandboxMode()) return sandboxRead(collName);
  const snap = await getDocs(collRef(collName));
  return snap.docs.map((d) => ({ idFirebase: d.id, ...d.data() }));
};

// Create a doc with a client-generated UUID so we can keep stable IDs across
// the localStorage -> Firestore migration.
export const createDocWithId = async (collName, data, id = uuidv4()) => {
  if (isSandboxMode()) {
    sandboxWriteDoc(collName, id, cleanData({ ...data, idFirebase: id }));
    return id;
  }
  await setDoc(docRef(collName, id), cleanData({ ...data, idFirebase: id }));
  return id;
};

export const updateDocById = async (collName, id, data) => {
  if (isSandboxMode()) {
    sandboxWriteDoc(collName, id, cleanData(data));
    return;
  }
  await updateDoc(docRef(collName, id), cleanData(data));
};

export const deleteDocById = async (collName, id) => {
  if (isSandboxMode()) {
    const items = sandboxRead(collName).filter((i) => i.idFirebase !== id);
    sandboxWrite(collName, items);
    return;
  }
  await deleteDoc(docRef(collName, id));
};

export const getDocById = async (collName, id) => {
  if (isSandboxMode()) return sandboxReadDoc(collName, id);
  const snap = await getDoc(docRef(collName, id));
  return snap.exists() ? { idFirebase: snap.id, ...snap.data() } : null;
};

// Singleton config: stored as a one-element array under the same coll key.
export const getSingleton = async (collName, docId, defaults = {}) => {
  if (isSandboxMode()) {
    const found = sandboxReadDoc(collName, docId);
    if (!found) return { ...defaults };
    const { idFirebase, ...rest } = found;
    return { ...defaults, ...rest };
  }
  const snap = await getDoc(docRef(collName, docId));
  return snap.exists() ? { ...defaults, ...snap.data() } : { ...defaults };
};

export const setSingleton = async (collName, docId, data) => {
  if (isSandboxMode()) {
    const existing = sandboxReadDoc(collName, docId) || {};
    sandboxWriteDoc(collName, docId, { ...existing, ...cleanData(data) });
    return;
  }
  await setDoc(docRef(collName, docId), cleanData(data), { merge: true });
};

// Run setDoc for many items in chunks of 500 (Firestore batch limit).
// Each item must already include `idFirebase` (used as the doc id).
export const batchSet = async (collName, items) => {
  if (!items?.length) return 0;
  if (isSandboxMode()) {
    const cleaned = items.map((it) => {
      const id = it.idFirebase || uuidv4();
      return cleanData({ ...it, idFirebase: id });
    });
    // Append; we don't dedup here because callers (importer, migrator) already
    // build deduped lists upstream.
    const existing = sandboxRead(collName);
    sandboxWrite(collName, [...existing, ...cleaned]);
    return items.length;
  }
  const CHUNK = 500;
  for (let i = 0; i < items.length; i += CHUNK) {
    const slice = items.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    for (const item of slice) {
      const id = item.idFirebase || uuidv4();
      batch.set(docRef(collName, id), cleanData({ ...item, idFirebase: id }));
    }
    await batch.commit();
  }
  return items.length;
};

// Patch the same `patch` onto every doc whose `field` equals `value`.
// Used to cascade denormalized names (e.g. locadorNombre/locatarioNombre that
// are copied onto each contrato) when the source record is renamed.
// Returns the number of docs updated.
export const updateManyByField = async (collName, field, value, patch) => {
  if (isSandboxMode()) {
    const items = sandboxRead(collName);
    let n = 0;
    for (const it of items) {
      if (it[field] === value) {
        Object.assign(it, cleanData(patch));
        n++;
      }
    }
    if (n) sandboxWrite(collName, items);
    return n;
  }
  const snap = await getDocs(query(collRef(collName), where(field, "==", value)));
  if (snap.empty) return 0;
  const docs = snap.docs;
  const CHUNK = 500;
  for (let i = 0; i < docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + CHUNK)) {
      batch.update(d.ref, cleanData(patch));
    }
    await batch.commit();
  }
  return docs.length;
};

// Find the first doc whose `field` equals `value`. Returns null if none.
export const findOneByField = async (collName, field, value) => {
  if (isSandboxMode()) {
    const items = sandboxRead(collName);
    return items.find((i) => i[field] === value) || null;
  }
  const q = query(collRef(collName), where(field, "==", value));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { idFirebase: d.id, ...d.data() };
};

export { uuidv4 };
